import { useState, useCallback, useEffect } from "react";
import {
  listCourseAssignments,
  updateAssignmentStatus,
  type CourseAssignment,
  type CourseAssignmentStatus,
} from "../../api/academyCourseAssignments/courseAssignmentsService";

export function useCourseAssignments() {
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatusState] = useState("");

  // Drawer state (create only)
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Revoke confirmation state
  const [revokeTarget, setRevokeTarget] = useState<CourseAssignment | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const fetchAssignments = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSearch: string,
      currentStatus: string
    ) => {
      setIsPageLoading(true);
      setError(null);
      try {
        const response = await listCourseAssignments({
          phoneNumber: currentSearch,
          status: currentStatus || undefined,
          pageNum: currentPage,
          pageSize: currentPageSize,
        });
        setAssignments(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(Math.max(1, Math.ceil(response.totalCount / currentPageSize)));
      } catch {
        setError("Failed to load course assignments");
      } finally {
        setIsPageLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchAssignments(page, pageSize, search, status);
  }, [page, pageSize, search, status, fetchAssignments]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setStatusState(value);
    setPage(1);
  }, []);

  // ── Create drawer ──────────────────────────────────────────────────────────
  const handleCreate = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerHide = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    fetchAssignments(page, pageSize, search, status);
  }, [page, pageSize, search, status, fetchAssignments]);

  // ── Immediate status change (Assign / Unassign) ────────────────────────────
  const handleChangeStatus = useCallback(
    async (assignment: CourseAssignment, newStatus: CourseAssignmentStatus) => {
      setError(null);
      try {
        await updateAssignmentStatus(assignment._id, newStatus);
        fetchAssignments(page, pageSize, search, status);
      } catch {
        setError("Failed to update assignment status");
      }
    },
    [page, pageSize, search, status, fetchAssignments]
  );

  // ── Revoke flow (with confirmation) ───────────────────────────────────────
  const handleRequestRevoke = useCallback((assignment: CourseAssignment) => {
    setRevokeError(null);
    setRevokeTarget(assignment);
  }, []);

  const handleCancelRevoke = useCallback(() => {
    setRevokeTarget(null);
    setRevokeError(null);
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    setRevokeError(null);
    try {
      await updateAssignmentStatus(revokeTarget._id, "REVOKED");
      setRevokeTarget(null);
      fetchAssignments(page, pageSize, search, status);
    } catch {
      setRevokeError("Failed to revoke assignment");
    } finally {
      setIsRevoking(false);
    }
  }, [revokeTarget, page, pageSize, search, status, fetchAssignments]);

  return {
    functions: {
      handleCreate,
      handleDrawerHide,
      handleDrawerSuccess,
      handleChangeStatus,
      handleRequestRevoke,
      handleCancelRevoke,
      handleConfirmRevoke,
    },
    values: {
      assignments,
      page,
      pageSize,
      totalCount,
      totalPages,
      status,
      drawerOpen,
      revokeTarget,
      isRevoking,
      revokeError,
    },
    loading: {
      isPageLoading,
    },
    error,
    setters: {
      setPage,
      setPageSize,
      setSearch: handleSearchChange,
      setStatus: handleStatusChange,
    },
  };
}
